import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { ActionLink } from "@/components/shared/action-buttons"
import { useIndexProps } from "./page-props"
import { useTranslation } from "react-i18next"
import {
  editEducatorsTeacherTeam,
  editEducatorsTeacherTeamTeamTimetable,
  educatorsTeacherTeam,
  educatorsTeacherTeamInvitations,
  educatorsTeacherTeamMembers,
  educatorsTeacherTeamTeamApplications,
  newEducatorsTeacherTeam,
} from "@/routes"
import {
  CalendarClock,
  Inbox,
  Pencil,
  PlusCircle,
  ScanSearch,
  Trash,
  Users,
  UserSearch,
} from "lucide-react"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Link, usePathParam } from "@/lib/page-rendering"

export default function Page() {
  const { teams } = useIndexProps()
  const { t } = useTranslation(["teams", "common"])
  const teacherId = usePathParam("teacherId")

  return (
    <>
      <EducatorPageChrome
        action={
          <ActionLink
            variant="solid"
            href={newEducatorsTeacherTeam({ teacherId }).url}
            Icon={PlusCircle}
          >
            {t("index.create")}
          </ActionLink>
        }
      />
      <PageBody role="region">
        <Card data-testid="team-list">
          {teams.length > 0 && (
            <CardHeader className="flex flex-row justify-end">
              <Link
                href="/search/educators?listing_type.keyword=team"
                as={Button}
              >
                <ScanSearch />
                {t("index.join")}
              </Link>
            </CardHeader>
          )}
          <CardContent className="grid grid-cols-2 gap-6 py-6">
            {teams.length == 0 ? (
              <Empty className="col-span-3">
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("index.empty_list_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("index.empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Link
                    href={newEducatorsTeacherTeam({ teacherId }).url}
                    as={Button}
                  >
                    <PlusCircle />
                    {t("index.create")}
                  </Link>
                  <Link
                    href="/search/educators?listing_type.keyword=team"
                    as={Button}
                  >
                    <ScanSearch />
                    {t("index.join")}
                  </Link>
                </EmptyContent>
              </Empty>
            ) : (
              teams.map((team) => (
                <Card key={team.id} className="w-full" data-testid="team-card">
                  <CardHeader className="flex flex-row items-center justify-between align-bottom">
                    <CardTitle data-testid="team-name">{team.name}</CardTitle>
                  </CardHeader>
                  <CardFooter className="flex flex-row flex-wrap gap-4">
                    <Link
                      href={
                        editEducatorsTeacherTeam({ teacherId, id: team.id }).url
                      }
                      as={Button}
                    >
                      <Pencil />
                      {t("common:actions.edit")}
                    </Link>
                    <Link
                      href={
                        educatorsTeacherTeamMembers({
                          teacherId,
                          teamId: team.id,
                        }).url
                      }
                      as={Button}
                    >
                      <Users />
                      {t("teams:index.members")}
                    </Link>
                    <Link
                      href={
                        educatorsTeacherTeamInvitations({
                          teacherId,
                          teamId: team.id,
                        }).url
                      }
                      as={Button}
                    >
                      <UserSearch />
                      {t("teams:index.invitations")}
                    </Link>
                    <Link
                      href={
                        educatorsTeacherTeamTeamApplications({
                          teacherId,
                          teamId: team.id,
                        }).url
                      }
                      as={Button}
                    >
                      <Inbox />
                      {t("teams:index.team_applications")}
                    </Link>
                    {team.shared && (
                      <Link
                        href={
                          editEducatorsTeacherTeamTeamTimetable({
                            teacherId,
                            teamId: team.id,
                          }).url
                        }
                        as={Button}
                      >
                        <CalendarClock />
                        {t("teams:index.team_timetable")}
                      </Link>
                    )}
                    <ConfirmationDialog
                      action={
                        <Link
                          className={buttonVariants({ variant: "destructive" })}
                          href={
                            educatorsTeacherTeam({ teacherId, id: team.id }).url
                          }
                          method="delete"
                          as={Button}
                        >
                          {t("common:actions.confirm")}
                        </Link>
                      }
                    >
                      <ConfirmationDialogTrigger asChild>
                        <Button variant={"destructive-outline"}>
                          <Trash />
                          {t("common:actions.delete")}
                        </Button>
                      </ConfirmationDialogTrigger>
                    </ConfirmationDialog>
                  </CardFooter>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}
